import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { RefreshCcw, Repeat, Search } from 'lucide-react'
import { apiClient } from '../services/api'
import { useTranslation } from '../contexts/TranslationContext'
import Button from '../components/ui/Button'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { ui } from '../styles/ui'
import { formatDateTime } from '../utils/date'
import { formatEmptyValue } from '../utils/displayValue'
import useNotificationStore from '../services/notificationStore'

export default function ProductTranslationQueue() {
  const { t } = useTranslation()
  const upsertOperation = useNotificationStore((state) => state.upsertOperation)
  const showToast = useNotificationStore((state) => state.showToast)
  const dismissToast = useNotificationStore((state) => state.dismissToast)
  const [receipts, setReceipts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('pending')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const loadReceipts = async (pageNumber = 1) => {
    try {
      setLoading(true)
      setError('')
      const params = { page: pageNumber }
      if (search) params.search = search
      if (status) params.status = status

      const response = await apiClient.get('/queue/product-translation', { params })
      setReceipts(response.data.data || [])
      setPage(response.data.current_page || 1)
      setTotalPages(response.data.last_page || 1)
    } catch (err) {
      setError(t('Failed to load product translation queue', 'Failed to load product translation queue'))
      console.error('Failed to load product translation queue', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReceipts(1)
  }, [])

  useEffect(() => {
    loadReceipts(1)
  }, [status])

  useEffect(() => {
    const interval = setInterval(() => {
      loadReceipts(page)
    }, 30000)

    return () => clearInterval(interval)
  }, [page, status, search])

  const retryReceipt = async (receiptId) => {
    const operationId = `product-translation-retry-${receiptId}`
    try {
      upsertOperation({
        id: operationId,
        type: 'product_translation_retry',
        status: 'running',
      })
      showToast({
        id: `operation-progress-${operationId}`,
        kind: 'operation-progress',
        severity: 'info',
        title: t('Retry queued', 'Retry queued'),
        message: t('Retrying translation pipeline for this receipt.', 'Retrying translation pipeline for this receipt.'),
        operationId,
        persistent: true,
      })
      await apiClient.post(`/queue/product-translation/${receiptId}/retry`)
      dismissToast(`operation-progress-${operationId}`)
      upsertOperation({
        id: operationId,
        type: 'product_translation_retry',
        status: 'completed',
      })
      showToast({
        id: `retry-success-${operationId}`,
        kind: 'notification',
        severity: 'success',
        title: t('Retry scheduled', 'Retry scheduled'),
        message: t('The receipt was queued for translation retry.', 'The receipt was queued for translation retry.'),
      })
      loadReceipts(page)
    } catch (err) {
      dismissToast(`operation-progress-${operationId}`)
      upsertOperation({
        id: operationId,
        type: 'product_translation_retry',
        status: 'failed',
      })
      showToast({
        id: `retry-failed-${operationId}`,
        kind: 'notification',
        severity: 'error',
        title: t('Retry failed', 'Retry failed'),
        message: t('Failed to retry translation for this receipt.', 'Failed to retry translation for this receipt.'),
        persistent: true,
      })
      console.error('Failed to retry product translation', err)
    }
  }

  const getStatusBadge = (s) => {
    if (!s) return <span className={`${ui.badge} ${ui.badgeNeutral}`}>{t('N/A', 'N/A')}</span>
    if (s === 'completed') return <span className={`${ui.badge} ${ui.badgeSuccess}`}>{s}</span>
    if (s === 'failed') return <span className={`${ui.badge} ${ui.badgeError}`}>{s}</span>
    if (s === 'processing') return <span className={`${ui.badge} ${ui.badgeWarning}`}>{s}</span>
    return <span className={`${ui.badge} ${ui.badgeNeutral}`}>{s}</span>
  }

  return (
    <PageShell>
      {/* Header */}
      <PageHeader
        title={t('Product Translation Queue', 'Product Translation Queue')}
        subtitle={t('Monitor receipts waiting for product name translation.', 'Monitor receipts waiting for product name translation.')}
        actions={
          <Button variant="ghost" onClick={() => loadReceipts(1)}>
            <RefreshCcw size={16} className="mr-2" /> {t('Refresh', 'Refresh')}
          </Button>
        }
      />

      {/* Filters */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className={`${ui.card} ${ui.cardBody}`}>
          <label className={`${ui.label} mb-2`}>{t('Search', 'Search')}</label>
          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadReceipts(1)}
              className={ui.input}
              placeholder={t('Receipt ID or seller', 'Receipt ID or seller')}
            />
            <Button type="submit" onClick={() => loadReceipts(1)}>
              <Search size={16} />
            </Button>
          </div>
        </div>

        <div className={`${ui.card} ${ui.cardBody}`}>
          <label className={`${ui.label} mb-2`}>{t('Status', 'Status')}</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={ui.select}
          >
            <option value="">{t('All', 'All')}</option>
            <option value="pending">{t('Pending', 'Pending')}</option>
            <option value="processing">{t('Processing', 'Processing')}</option>
            <option value="completed">{t('Completed', 'Completed')}</option>
            <option value="failed">{t('Failed', 'Failed')}</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && <div className={ui.alertError}>{error}</div>}

      {/* Table */}
      <div className={ui.card}>
        <table className={ui.table}>
          <thead className={ui.tableHead}>
            <tr>
              <th className={ui.tableHeadCell}>{t('Receipt ID', 'Receipt ID')}</th>
              <th className={ui.tableHeadCell}>{t('User', 'User')}</th>
              <th className={ui.tableHeadCell}>{t('Seller', 'Seller')}</th>
              <th className={ui.tableHeadCell}>{t('Translation Status', 'Translation Status')}</th>
              <th className={ui.tableHeadCell}>{t('OCR Status', 'OCR Status')}</th>
              <th className={ui.tableHeadCell}>{t('Created', 'Created')}</th>
              <th className={ui.tableHeadCell}>{t('Actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-divider">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center">
                  <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                    <div className={ui.spinner} />
                    <div className="text-sm">{t('Loading queue...', 'Loading queue...')}</div>
                  </div>
                </td>
              </tr>
            ) : receipts.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                  {t('No receipts found.', 'No receipts found.')}
                </td>
              </tr>
            ) : (
              receipts.map((receipt) => (
                <tr key={receipt.id} className="bg-surface hover:bg-surface-elevated">
                  <td className={ui.tableCell}><span className="font-medium text-foreground">{receipt.id}</span></td>
                  <td className={ui.tableCellText}>{receipt.user?.email || receipt.user?.name || t('N/A', 'N/A')}</td>
                  <td className={ui.tableCellText}>{formatEmptyValue(receipt.seller || receipt.file_name)}</td>
                  <td className={ui.tableCell}>{getStatusBadge(receipt.product_translation_status)}</td>
                  <td className={ui.tableCell}>{getStatusBadge(receipt.ocr_status)}</td>
                  <td className={ui.tableCellText}>{formatDateTime(receipt.created_at)}</td>
                  <td className={ui.tableCell}>
                    <div className="flex gap-1">
                      <Link to={`/admin/jobs/${receipt.id}`} className={ui.buttonSmGhost}>
                        {t('View', 'View')}
                      </Link>
                      <Button size="sm" onClick={() => retryReceipt(receipt.id)}>
                        <Repeat size={14} /> {t('Retry', 'Retry')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2 py-3 text-sm text-muted-foreground">
        <Button variant="ghost" onClick={() => loadReceipts(Math.max(1, page - 1))} disabled={page <= 1}>
          {t('Previous', 'Previous')}
        </Button>
        <span>{t('Page', 'Page')} {page} / {totalPages}</span>
        <Button variant="ghost" onClick={() => loadReceipts(Math.min(totalPages, page + 1))} disabled={page >= totalPages}>
          {t('Next', 'Next')}
        </Button>
      </div>
    </PageShell>
  )
}

